import Minilog from 'minilog';

import { TEXT_ERROR_COUNTRIES } from './locale';
import {getStackTrace} from './core/exceptions';
import {addErrorMessage} from './feedback';
import {NewV2Resource, V2ListModel, SuccessResponse} from './services';
import {createMergeAction} from './base';
import {mergeObjects} from './core/objects';
import List = _.List;

let logger = Minilog('frontend.models.geo');

export interface V2Country {
    id: number;
    name: string;
    icon?: string;
    resource_uri?: string;
}

export interface V2Region {
    id: number;
    name: string;
    country: number;
    resource_uri?: string;
}

export enum LocationFilterLevel {
    None, Country, Region, Premises
}

/**
 * Regions stored in a map of region IDs to region data, along with a sorted
 * list of region IDs by name and a map of country IDs to region IDs.
 */
export interface CountryState {
    data: Map<number, V2Country>;
    sortedByName: Array<number>;
}

/**
 * Regions stored in a map of region IDs to region data, along with a sorted
 * list of region IDs by name and a map of country IDs to region IDs.
 */
export interface RegionState {
    data: Map<number, V2Region>;
    sortedByName: Array<number>;
    byCountry: Map<number, Array<number>>;
}

/**
 * For handling all regions v2 API calls
 */
class RegionResource extends NewV2Resource {
    constructor() {
        super(NewV2Resource.REGION);
    }
}

/**
 * For handling all regions v2 API calls
 */
class CountryResource extends NewV2Resource {
    constructor() {
        super(NewV2Resource.COUNTRY);
    }
}

export var RegionAPI = new RegionResource();
export var CountryAPI = new CountryResource();

function enableGeoLoad() {
    return createMergeAction(
        'countries_load',
        {ui: {geo: {loading: true}}}
    );
}

function disableGeoLoad() {
    return createMergeAction(
        'countries_loaded',
        {ui: {geo: {loading: false}}}
    );
}

function alphabetical(a, b) {
    if (a < b) return -1;
    else if (a > b) return 1;
    return 0;
}

/***
 * Extracts normalised countries and regions from regions API response.
 */
function setCountriesAndRegions(countriesResponse: Array<V2Country>,
                                regionsResponse: Array<V2Region>) {
    // shallow clone array, JS sort works in place.
    let sortedCountries = countriesResponse.slice();
    let sortedRegions = regionsResponse.slice();
    let countries = {}, countriesSortedByName = [];
    let regionsByCountry = {}, regions = {}, regionsSortedByName = [];

    // Sort countries alphabetically
    sortedCountries.sort((a, b) => alphabetical(a.name, b.name));
    // Sort regions alphabetically
    sortedRegions.sort((a, b) => alphabetical(a.name, b.name));

    // Extract country data
    sortedCountries.forEach((country) => {
        countriesSortedByName.push(country.id);
        countries[country.id] = country;
    });

    // extract region data
    sortedRegions.forEach(region => {
        // Create indexes
        regionsSortedByName.push(region.id);
        regions[region.id] = region;
        regionsByCountry[region.country] = regionsByCountry[region.country] || [];
        regionsByCountry[region.country].push(region.id);
    });

    return createMergeAction(
        'countries_regions_set',
        {
            data: {
                countries: {
                    data: countries,
                    sortedByName: countriesSortedByName
                },
                regions: {
                    data: regions,
                    sortedByName: regionsSortedByName,
                    byCountry: regionsByCountry
                }
            }
        },
        {array_replace: true}
    );
}

/**
 * Reads the countries and regions available to the user from the API and stores
 * the result in the global store.
 *
 * @returns {function(any): Promise<TResult>|Promise<T>}
 */
export function getCountryRegions(regionFilters?: any) {
     return dispatch => {
        dispatch(enableGeoLoad());
        return CountryAPI.listAll<V2ListModel<V2Country>>(regionFilters).then((countriesResponse) => {
            return RegionAPI.listAll<V2ListModel<V2Region>>(regionFilters).then((regionsResponse) => {
                dispatch(
                    setCountriesAndRegions(countriesResponse.data, regionsResponse.data)
                );
                dispatch(disableGeoLoad());
            });
        }).catch(function(error) {
            logger.error(`Failed to load country/region data: ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(TEXT_ERROR_COUNTRIES));
            dispatch(disableGeoLoad());
        });
    };
}

export function listRegionsByCountry(regions, countryCode: string | number) {
     return regions.byCountry[countryCode] || [];
}
